/**
 * HUD: layer toggles, readouts, and the mark editor.
 *
 * There is no transport. The board is live-only, so the controls that remain
 * are about what you can see and what you can annotate.
 */

import { isShortcut } from "./keys.js";
import { markTypes } from "../layers/marks.js";

const el = (id) => document.getElementById(id);

function toggleRow({ name, detail, pressed, title, onToggle }) {
  const row = document.createElement("div");
  row.className = "toggle";
  row.setAttribute("role", "button");
  row.setAttribute("tabindex", "0");
  row.setAttribute("aria-pressed", String(pressed));
  if (title) row.title = title;

  const box = document.createElement("span");
  box.className = "toggle-box";
  const label = document.createElement("span");
  label.className = "toggle-name";
  label.textContent = name;
  const count = document.createElement("span");
  count.className = "toggle-count";
  count.textContent = detail;
  row.append(box, label, count);

  const flip = () => {
    const next = row.getAttribute("aria-pressed") !== "true";
    row.setAttribute("aria-pressed", String(next));
    onToggle(next);
  };

  row.addEventListener("click", flip);
  row.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      flip();
    }
  });

  return { row, setDetail: (text) => { count.textContent = text; } };
}

/** "THU 16:11" — UTC, tabular, no locale surprises. */
/**
 * Denver time, because that is when these things happened.
 *
 * This used to render UTC without saying so, which put two different
 * timezones in adjacent rows of the same panel once the window range appeared
 * beside it — six hours apart, both unlabelled. The zone is pinned to the city
 * rather than taken from the viewer so the readout means the same thing to
 * someone reading it from another one.
 */
export const CITY_TZ = "America/Denver";

export function formatClock(epochMs) {
  if (!Number.isFinite(epochMs)) return "--:--";
  const parts = new Date(epochMs).toLocaleString("en-US", {
    timeZone: CITY_TZ, weekday: "short", hour: "2-digit", minute: "2-digit",
    hour12: false,
  });
  // "Thu, 10:11" -> "THU 10:11"
  return parts.replace(",", "").toUpperCase();
}

/**
 * The span the visible incidents actually cover, in Denver time.
 *
 * The panel used to be headed "Last 24h", which was not true. The window is
 * 24 hours wide but it ends at the newest record, and Denver's feed publishes
 * several days behind — so "last 24h" was really "some day last week". Naming
 * the two ends is the only version of this that cannot mislead.
 */
export function formatWindow(rows) {
  if (!rows?.length) return "no records";

  const first = new Date(rows[0].t);
  const last = new Date(rows[rows.length - 1].t);
  const day = { timeZone: CITY_TZ, month: "short", day: "numeric" };
  const time = { timeZone: CITY_TZ, hour: "2-digit", minute: "2-digit", hour12: false };
  const dayOf = (d) => d.toLocaleDateString("en-US", day);

  const from = `${dayOf(first)} ${first.toLocaleTimeString("en-US", time)}`;
  // Repeating the date for a window inside one day is just noise.
  const to = dayOf(first) === dayOf(last)
    ? last.toLocaleTimeString("en-US", time)
    : `${dayOf(last)} ${last.toLocaleTimeString("en-US", time)}`;

  return `${from} → ${to}`;
}

/** Human age of the newest record. Exported so it can be unit-tested. */
export function formatLag(latest, nowMs = Date.now()) {
  if (!latest) return "";
  const hours = Math.round((nowMs - latest) / 3600000);
  if (hours < 1) return "CURRENT";
  if (hours < 48) return `LAG ${hours}H`;
  return `LAG ${Math.round(hours / 24)}D`;
}

export function mountHud({
  buildingEdits, layers, marks, map, getNow, latest, buildings, onChange }) {
  let identity = { user: null, authenticated: false };
  let chosenColor = "blue";
  const toggles = el("layer-toggles");
  const legend = el("legend");
  const rows = [];

  // ── Layer toggles ───────────────────────────────────────────────────────
  //
  // Two things are deliberate here.
  //
  // Some layers have no toggle at all. Land use, buildings, entrances and
  // police are the fabric of the map rather than things you switch between —
  // turning them off leaves a worse map, not a different view — so they are
  // always on and take no row.
  //
  // And a layer is never disabled for being slow. Layers load after the first
  // paint now, so at mount most are still idle; switching them off then left
  // them off forever, because nothing turned them back on. render() already
  // refuses to draw a layer that is not ready, which is the correct place for
  // that decision.
  const PINNED = new Set(["landuse", "entrances", "police", "barriers"]);

  /** One row may drive several layers where the distinction is ours, not the reader's. */
  const GROUPS = [
    {
      name: "ROAD WORK",
      keys: ["streetwork", "cotrip"],
      title: "Street closures, construction permits and CDOT highway conditions",
    },
    { name: "CAMERAS", keys: ["cameras"] },
    { name: "EVENTS", keys: ["events"] },
    { name: "INCIDENTS", keys: ["incidents"] },
  ];

  const byKey = new Map(layers.map((l) => [l.key, l]));

  for (const layer of layers) {
    if (PINNED.has(layer.key)) layer.enabled = true;
  }

  for (const group of GROUPS) {
    const members = group.keys.map((k) => byKey.get(k)).filter(Boolean);
    if (!members.length) continue;

    const control = toggleRow({
      name: group.name,
      detail: "…",
      pressed: true,
      title: group.title ?? members[0].constructor.description ?? "",
      onToggle: (on) => {
        for (const layer of members) layer.enabled = on;
        onChange();
      },
    });
    toggles.append(control.row);
    rows.push({ members, control });
  }

  const markToggle = toggleRow({
    name: "MARKS",
    detail: String(marks.count),
    pressed: true,
    title: "Your saved marks and notes",
    onToggle: (on) => { marks.enabled = on; onChange(); },
  });
  toggles.append(markToggle.row);

  const viewshedToggle = toggleRow({
    name: "CAMERA VIEW",
    detail: "OFF",
    pressed: buildings.viewshedOn?.() ?? false,
    title: "Area around each on-screen camera with no building in the way. "
         + "Unobstructed range, not field of view — OSM tags direction on "
         + "almost no cameras, so which way they point is unknown. "
         + "Appears once you are zoomed in past street level.",
    onToggle: (on) => {
      buildings.toggleViewshed?.(on);
      viewshedToggle.setDetail(on ? "ON" : "OFF");
    },
  });
  toggles.append(viewshedToggle.row);

  el("layer-count").textContent = String(toggles.children.length);

  // ── Legend ──────────────────────────────────────────────────────────────
  // Rebuilt to show the colours actually on the map. The old one used the
  // greyscale density blocks inherited from the previous project, which said
  // nothing once the map gained teal incidents, purple cameras and green
  // parks — it was decorative at best and misleading at worst.
  /**
   * One legend row. Given a `filter`, it becomes a button that hides its kind.
   *
   * A real <button>, not a div with a click handler: these are controls now,
   * and the keyboard and screen-reader behaviour that comes free with the
   * element is the whole reason to use it.
   */
  function swatchRow(rgb, label, note, filter) {
    const row = document.createElement(filter ? "button" : "div");
    row.className = "legend";
    if (filter) {
      row.type = "button";
      row.classList.add("legend-filter");
    }
    const chip = document.createElement("span");
    chip.className = "legend-chip";
    chip.style.background = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    const text = document.createElement("span");
    text.textContent = label;
    row.append(chip, text);
    if (note) row.title = note;

    if (filter) {
      const { layer, key } = filter;
      const paintRow = () => {
        const hidden = layer.isHidden(key);
        // aria-pressed says "this filter is engaged", so it is true when the
        // kind is HIDDEN — the dimmed chip is the same statement in paint.
        row.setAttribute("aria-pressed", String(hidden));
        row.classList.toggle("is-off", hidden);
        row.title = hidden
          ? `${label} is hidden. Click to show it again.`
          : `${note ? `${note}\n\n` : ""}Click to hide ${label.toLowerCase()}.`;
      };
      paintRow();
      row.addEventListener("click", () => {
        layer.toggleKind(key);
        paintRow();
        // A filter change is a layer-toggle-class event, and a full repaint is
        // already how those are handled.
        onChange?.();
      });
    }

    legend.append(row);
    return row;
  }

  /**
   * Rebuild the legend from the layers' current colours.
   *
   * A function rather than a one-off loop at mount, because those colours
   * belong to the theme and the theme can change underneath it. Built once and
   * left alone, the legend kept whichever palette was active on load and
   * quietly described the wrong map.
   */
  function paintLegend() {
    legend.innerHTML = "";
    for (const layer of layers) {
      for (const entry of layer.legend?.() ?? []) {
        if (!entry.rgb) continue;
        // Only a keyed entry is clickable. An unkeyed one covers everything
        // its layer draws, so filtering it would just be the layer toggle
        // again — see Layer#legend for why that line is drawn there.
        swatchRow(entry.rgb, entry.label, entry.note,
          entry.key ? { layer, key: entry.key } : null);
      }
    }
    for (const [key, type] of Object.entries(markTypes())) {
      swatchRow(type.core, type.label, type.hint, { layer: marks, key });
    }
  }

  paintLegend();


  // ── Identity ────────────────────────────────────────────────────────────
  // Reads are public, so "not signed in" is a normal state, not an error. The
  // button reflects it and offers the prompt on demand.
  const loginButton = el("login");

  function paintIdentity() {
    if (!loginButton) return;
    if (identity.authenticated) {
      loginButton.textContent = identity.user;
      loginButton.setAttribute("aria-pressed", "true");
      loginButton.title = `Signed in as ${identity.user} — click to sign out`;
    } else {
      loginButton.textContent = "SIGN IN";
      loginButton.setAttribute("aria-pressed", "false");
      loginButton.title = "Sign in to add marks and files";
    }
  }

  async function refreshIdentity() {
    try {
      const response = await fetch("./api/whoami");
      identity = await response.json();
    } catch {
      identity = { user: null, authenticated: false };
    }
    paintIdentity();
  }

  /**
   * True once the viewer is signed in, prompting them if they are not.
   *
   * Every edit affordance runs through here BEFORE opening anything: writes
   * have required auth at the server all along, but the interface used to
   * offer the whole editing flow to an anonymous viewer and only fail at the
   * save button — a form's worth of typing thrown away to learn something
   * the first tap already knew.
   */
  async function ensureSignedIn() {
    if (identity.authenticated) return true;
    // Under basic auth this fetched the one authenticated GET purely to make
    // the browser raise its credential prompt. A session has no prompt to
    // raise — signing in is a page — so the honest move is to leave for it and
    // come back here. Nothing is returned to a caller that is navigating away,
    // but false is still correct for the moment before the page unloads.
    const here = window.location.pathname + window.location.search;
    window.location.assign(`/auth/login?next=${encodeURIComponent(here)}`);
    return false;
  }

  // Signed out, the button signs you in. Signed in, it signs you out and
  // returns here — the map stays readable, the edit affordances simply go.
  // Without this a signed-in visitor had no way off the session at all.
  loginButton?.addEventListener("click", () => {
    if (identity.authenticated) {
      window.location.assign("/auth/logout?next=/opsec/");
    } else {
      ensureSignedIn();
    }
  });

  // Denver's reporting pipeline lags. Saying so is the difference between a
  // live board and one that quietly pretends to be.
  //
  // The readout stays pinned to the OFFICIAL records even though live sources
  // now sit in the same stream. A blended "newest of everything" would read
  // CURRENT the moment a power cut arrived and would be telling the reader
  // that Denver's police data is current, which is the one thing this readout
  // exists to deny. The live sources get their own line in the tooltip.
  const lagEl = el("lag");
  if (lagEl) {
    lagEl.textContent = formatLag(latest);
    lagEl.title = "Age of the newest official record in the feed.";
  }

  // ── Exposure ────────────────────────────────────────────────────────────
  // Shodan, as city totals and nothing else. It geolocates most hosts to their
  // ISP's registered address — 97.7% of Denver's land in one square kilometre
  // downtown — so a count is a true statement about the city and a position
  // would be a fabrication.
  (async () => {
    const host = el("shodan");
    const totalEl = el("shodan-total");
    if (!host) return;
    try {
      const body = await (await fetch("./api/shodan")).json();
      if (!body.configured) {
        totalEl.textContent = "OFF";
        host.textContent = "No Shodan key set.";
        return;
      }
      // Every number here is a Shodan query, so every number is a link.
      //
      // This was a bare count and five port chips, with the org and product
      // facets fetched and then thrown away, and nothing clickable — which
      // makes it a statistic rather than a starting point. Opening the search
      // that produced a number is the only way to act on it.
      const BASE = 'city:"Denver"';
      const search = (q) => `https://www.shodan.io/search?query=${encodeURIComponent(q)}`;

      const link = (text, query, title, className) => {
        const a = document.createElement("a");
        if (className) a.className = className;
        a.href = search(query);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = text;
        a.title = `${title} — opens on Shodan`;
        return a;
      };

      totalEl.title = body.note ?? "";
      totalEl.replaceChildren(link(
        body.total.toLocaleString(), BASE,
        `All ${body.total.toLocaleString()} hosts Shodan sees in Denver`,
      ));

      host.innerHTML = "";
      const chip = (text, query, title) =>
        host.append(link(text, query, title, "chip-link"));

      for (const port of (body.ports ?? []).slice(0, 6)) {
        chip(`:${port.port} ${port.count.toLocaleString()}`,
             `${BASE} port:${port.port}`,
             `${port.count.toLocaleString()} hosts with port ${port.port} open`);
      }
      if (body.with_screenshot) {
        chip(`screens ${body.with_screenshot.toLocaleString()}`,
             `${BASE} has_screenshot:true`,
             "Hosts exposing a screenshot — mostly remote desktop, not webcams");
      }
      // Who hosts them, and what is answering. Both were already in the
      // response and neither was ever shown.
      for (const org of (body.orgs ?? []).slice(0, 4)) {
        chip(`${org.org} ${org.count.toLocaleString()}`,
             `${BASE} org:"${org.org}"`,
             `${org.count.toLocaleString()} hosts on ${org.org}`);
      }
      for (const product of (body.products ?? []).slice(0, 4)) {
        chip(`${product.product} ${product.count.toLocaleString()}`,
             `${BASE} product:"${product.product}"`,
             `${product.count.toLocaleString()} hosts running ${product.product}`);
      }
    } catch {
      totalEl.textContent = "—";
      host.textContent = "Exposure data unavailable.";
    }
  })();

  refreshIdentity();

  // ── Mark editor ─────────────────────────────────────────────────────────
  const editor = el("mark-editor");
  const labelInput = el("mark-label");
  const noteInput = el("mark-note");
  const errorEl = el("mark-error");
  const deleteButton = el("mark-delete");
  const addButton = el("add-mark");

  let placing = false;
  let editing = null; // an existing mark
  let pending = null; // {lng, lat} for a new one

  function armHint(text) {
    let hint = el("place-hint");
    if (!hint) {
      hint = document.createElement("div");
      hint.id = "place-hint";
      hint.className = "place-hint";
      document.body.append(hint);
    }
    hint.textContent = text ?? "";
    hint.hidden = !text;
  }

  function setPlacing(on) {
    // Arming is the first step of a write, so it is where the sign-in gate
    // lives — one place, whichever button or key asked.
    if (on && !identity.authenticated) {
      ensureSignedIn().then((ok) => { if (ok) setPlacing(true); });
      return;
    }
    placing = on;
    addButton.setAttribute("aria-pressed", String(on));
    map.getCanvas().style.cursor = on ? "crosshair" : "";
    armHint(on ? "Tap the map to place a mark" : null);
  }

  const swatchWrap = el("mark-colors");
  for (const [key, type] of Object.entries(markTypes())) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "swatch";
    swatch.dataset.color = key;
    swatch.title = `${type.label} — ${type.hint}`;
    swatch.setAttribute("aria-pressed", String(key === chosenColor));
    swatch.append(document.createElement("i"));
    swatch.addEventListener("click", () => setColor(key));
    swatchWrap.append(swatch);
  }

  function setColor(color) {
    chosenColor = markTypes()[color] ? color : "blue";
    for (const swatch of swatchWrap.children) {
      swatch.setAttribute("aria-pressed", String(swatch.dataset.color === chosenColor));
    }
    // Colour alone does not say what a type is for; the hint does.
    const type = markTypes()[chosenColor];
    el("mark-type-hint").textContent = `${type.label} — ${type.hint}`;
  }

  /** Attachment rows. Type is always shown so nothing downloads blind. */
  function paintFiles(mark) {
    const field = el("mark-files-field");
    const list = el("mark-files");
    if (!mark) { field.hidden = true; return; }
    field.hidden = false;
    list.innerHTML = "";

    for (const file of mark.attachments ?? []) {
      const row = document.createElement("div");
      row.className = "filerow";

      const kind = document.createElement("span");
      kind.className = "filekind";
      kind.textContent = file.kind || "FILE";

      const link = document.createElement("a");
      link.href = marks.fileUrl(file.id);
      // textContent, never innerHTML: the filename is whatever the uploader
      // typed and is displayed verbatim on purpose.
      link.textContent = file.filename;
      link.title = `Download ${file.filename}`;

      const size = document.createElement("span");
      size.className = "filesize";
      size.textContent = `${Math.max(1, Math.round(file.bytes / 1024))} KB`;

      row.append(kind, link, size);

      if (identity.authenticated) {
        const remove = document.createElement("button");
        remove.className = "link-btn";
        remove.textContent = "×";
        remove.title = "Remove this file";
        remove.addEventListener("click", async () => {
          try {
            await marks.detach(file.id, mark.id);
            paintFiles(mark);
          } catch (error) {
            errorEl.textContent = error.message;
            errorEl.hidden = false;
          }
        });
        row.append(remove);
      }
      list.append(row);
    }
  }

  el("mark-file-input")?.addEventListener("change", async (event) => {
    const file = event.target.files?.[0];
    if (!file || !editing) return;
    errorEl.hidden = true;
    try {
      await marks.attach(editing.id, file);
      paintFiles(editing);
      event.target.value = "";
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });

  // ── Building editor ─────────────────────────────────────────────────────
  const bEditor = el("building-editor");
  const bError = el("building-error");
  const BUILDING_FIELDS = ["name", "use", "floors", "access", "status", "hours"];

  // Two tints, matching the server's whitelist. Red is what police buildings
  // already use, so a user marking one red is saying the same thing the map
  // says about the stations it knows about.
  const BUILDING_TINTS = [
    { key: "", label: "None", hint: "Leave the building shaded by height." },
    { key: "red", label: "Red", hint: "Same red the map uses for police buildings." },
    { key: "blue", label: "Blue", hint: "Deep blue." },
  ];
  let chosenTint = "";

  const tintWrap = el("bf-tint");
  for (const tint of BUILDING_TINTS) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.className = "swatch swatch--tint";
    swatch.dataset.tint = tint.key || "none";
    swatch.title = `${tint.label} — ${tint.hint}`;
    swatch.append(document.createElement("i"));
    swatch.addEventListener("click", () => setTint(tint.key));
    tintWrap?.append(swatch);
  }

  function setTint(value) {
    chosenTint = BUILDING_TINTS.some((t) => t.key === value) ? value : "";
    for (const swatch of tintWrap?.children ?? []) {
      const key = swatch.dataset.tint === "none" ? "" : swatch.dataset.tint;
      swatch.setAttribute("aria-pressed", String(key === chosenTint));
    }
    const chosen = BUILDING_TINTS.find((t) => t.key === chosenTint);
    el("bf-tint-hint").textContent = chosen ? chosen.hint : "";
  }
  let bCentroid = null;   // which footprint is open
  let bRecord = null;     // the saved contribution, once there is one

  function paintBuildingFiles() {
    const field = el("building-files-field");
    const list = el("building-files");
    // Files attach to a saved record, so the field appears once one exists.
    if (!bRecord) { field.hidden = true; return; }
    field.hidden = false;
    list.innerHTML = "";

    for (const file of bRecord.attachments ?? []) {
      const row = document.createElement("div");
      row.className = "filerow";

      const kind = document.createElement("span");
      kind.className = "filekind";
      kind.textContent = file.kind || "FILE";

      const link = document.createElement("a");
      link.href = `./api/files/${encodeURIComponent(file.id)}`;
      // textContent, never innerHTML: the filename is whatever the uploader
      // typed and is shown verbatim on purpose.
      link.textContent = file.filename;
      link.title = `Download ${file.filename}`;

      const size = document.createElement("span");
      size.className = "filesize";
      size.textContent = `${Math.max(1, Math.round(file.bytes / 1024))} KB`;

      row.append(kind, link, size);

      if (identity.authenticated) {
        const remove = document.createElement("button");
        remove.className = "link-btn";
        remove.textContent = "×";
        remove.title = "Remove this file";
        remove.addEventListener("click", async () => {
          try {
            const response = await fetch(`./api/files/${encodeURIComponent(file.id)}`,
                                         { method: "DELETE" });
            if (!response.ok) throw new Error(`Could not delete (${response.status})`);
            bRecord.attachments = (bRecord.attachments ?? [])
              .filter((f) => f.id !== file.id);
            paintBuildingFiles();
          } catch (error) {
            bError.textContent = error.message;
            bError.hidden = false;
          }
        });
        row.append(remove);
      }
      list.append(row);
    }
  }

  el("building-file-input")?.addEventListener("change", async (event) => {
    const file = event.target.files?.[0];
    if (!file || !bRecord) return;
    bError.hidden = true;
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await fetch(
        `./api/contributions/${encodeURIComponent(bRecord.id)}/files`,
        { method: "POST", body });
      const payload = await response.json().catch(() => null);
      if (response.status === 401 || response.status === 403) {
        throw new Error("Sign in to attach files");
      }
      if (!response.ok) throw new Error(payload?.error || `Upload failed (${response.status})`);
      bRecord.attachments = [...(bRecord.attachments ?? []), payload];
      paintBuildingFiles();
      event.target.value = "";
    } catch (error) {
      bError.textContent = error.message;
      bError.hidden = false;
    }
  });

  function closeBuildingEditor() {
    bEditor.hidden = true;
    bCentroid = null;
    bRecord = null;
    bError.hidden = true;
  }

  function openBuildingEditor({ centroid, survey }) {
    bCentroid = centroid;
    bRecord = buildingEdits.find(centroid);
    const fields = bRecord?.fields ?? {};

    for (const key of BUILDING_FIELDS) {
      el(`bf-${key}`).value = fields[key] ?? "";
    }
    el("bf-note").value = bRecord?.detail ?? "";
    setTint(fields.tint ?? "");

    // What the city actually surveyed, so an edit is a correction to something
    // visible rather than a guess into a void.
    el("building-survey").textContent = survey ?? "";
    el("building-attrib").textContent = bRecord
      ? `edited by ${bRecord.author || "—"}`
      : "no edits yet";
    el("building-delete").hidden = !bRecord;
    paintBuildingFiles();
    bError.hidden = true;
    bEditor.hidden = false;
    el("bf-name").focus();
  }

  el("building-close").addEventListener("click", closeBuildingEditor);

  el("building-save").addEventListener("click", async () => {
    if (!bCentroid) return;
    bError.hidden = true;
    const fields = { note: el("bf-note").value.trim(), tint: chosenTint };
    for (const key of BUILDING_FIELDS) {
      fields[key] = el(`bf-${key}`).value.trim();
    }
    try {
      bRecord = await buildingEdits.save(bCentroid, fields);
      onChange();
      // Saving is the end of the task. It used to stay open so files could be
      // attached straight away, but that left a panel over the building you
      // had just changed and no obvious signal the save had worked. Reopen it
      // to attach a file — the record is there now, so the file field is too.
      closeBuildingEditor();
    } catch (error) {
      bError.textContent = error.message;
      bError.hidden = false;
    }
  });

  el("building-delete").addEventListener("click", async () => {
    if (!bCentroid) return;
    try {
      await buildingEdits.remove(bCentroid);
      closeBuildingEditor();
      onChange();
    } catch (error) {
      bError.textContent = error.message;
      bError.hidden = false;
    }
  });

  window.addEventListener("pulse:edit-building", async (event) => {
    // The editor is a write from its first field; the reading view of a
    // building is the tooltip, which anonymous viewers already have.
    if (await ensureSignedIn()) openBuildingEditor(event.detail);
  });

  // ── Camera reports ──────────────────────────────────────────────────────
  // OSM carries 875 cameras downtown, which is a floor rather than a census —
  // somebody had to walk past each one. This is how the next one gets added.
  const camEditor = el("camera-editor");
  const camError = el("camera-error");
  const camButton = el("add-camera");
  let placingCamera = false;
  let camAt = null;

  function setPlacingCamera(on) {
    // Same gate as marks: arming a report is the start of a write.
    if (on && !identity.authenticated) {
      ensureSignedIn().then((ok) => { if (ok) setPlacingCamera(true); });
      return;
    }
    placingCamera = on;
    camButton?.setAttribute("aria-pressed", String(on));
    map.getCanvas().style.cursor = on ? "crosshair" : "";
    armHint(on ? "Tap the map to report a camera" : null);
    // The two placement modes are mutually exclusive; one click, one meaning.
    if (on && placing) setPlacing(false);
  }

  function openCameraEditor(at) {
    camAt = at;
    el("cam-label").value = "";
    el("cam-detail").value = "";
    el("camera-coords").textContent = `${at.lat.toFixed(5)}, ${at.lng.toFixed(5)}`;
    camError.hidden = true;
    camEditor.hidden = false;
    el("cam-label").focus();
  }

  function closeCameraEditor() {
    camEditor.hidden = true;
    camAt = null;
    camError.hidden = true;
  }

  camButton?.addEventListener("click", () => setPlacingCamera(!placingCamera));
  // Mobile duplicates. Same state, so arming from either place is identical.
  el("add-camera-mobile")?.addEventListener("click", () => setPlacingCamera(true));
  el("add-mark-mobile")?.addEventListener("click", () => {
    if (placingCamera) setPlacingCamera(false);
    setPlacing(true);
  });
  el("camera-close")?.addEventListener("click", closeCameraEditor);

  el("camera-save")?.addEventListener("click", async () => {
    if (!camAt) return;
    camError.hidden = true;
    try {
      const response = await fetch("./api/contributions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "camera",
          lng: camAt.lng,
          lat: camAt.lat,
          label: el("cam-label").value.trim(),
          detail: el("cam-detail").value.trim(),
        }),
      });
      if (response.status === 401 || response.status === 403) {
        throw new Error("Sign in to report a camera");
      }
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || `Could not save (${response.status})`);

      // Show it immediately rather than making the reporter reload to see it.
      layers.find((l) => l.key === "cameras")?.addUserCamera?.(payload);
      closeCameraEditor();
      onChange();
      tick();
    } catch (error) {
      camError.textContent = error.message;
      camError.hidden = false;
    }
  });

  function closeEditor() {
    editor.hidden = true;
    editing = null;
    pending = null;
    errorEl.hidden = true;
  }

  function openEditor({ mark, at }) {
    editing = mark ?? null;
    pending = at ?? null;
    el("mark-editor-title").textContent = mark ? "Edit mark" : "New mark";
    labelInput.value = mark?.label ?? "";
    noteInput.value = mark?.note ?? "";
    const lng = mark ? mark.lng : at.lng;
    const lat = mark ? mark.lat : at.lat;
    el("mark-coords").textContent = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    setColor(mark?.color ?? "blue");
    el("mark-expires").value = (mark?.expires_at ?? "").slice(0, 10);
    // Files attach to an existing mark, so the field only appears once saved.
    paintFiles(mark ?? null);
    deleteButton.hidden = !mark;
    errorEl.hidden = true;
    editor.hidden = false;
    labelInput.focus();
  }

  addButton.addEventListener("click", () => {
    if (placingCamera) setPlacingCamera(false);
    setPlacing(!placing);
  });
  el("mark-close").addEventListener("click", closeEditor);

  /**
   * Place whatever is armed at the point that was tapped.
   *
   * Bound to touchend as well as click. On a phone the synthetic click can be
   * swallowed — by the tooltip's own touch handling, or by the browser when a
   * tap lands during a gesture — and when it was, arming MARK or CAM did
   * nothing at all and there was no way to tell why.
   */
  let lastPlacedAt = 0;

  function placeAt(lngLat) {
    // Routing gets first refusal — if it is armed, the click was for it.
    let claimed = false;
    window.dispatchEvent(new CustomEvent("pulse:map-click", {
      detail: Object.assign(lngLat, { claim: () => { claimed = true; } }),
    }));
    if (claimed) return;
    if (!placing && !placingCamera) return;
    // click follows touchend on some browsers; ignore the echo.
    const now = Date.now();
    if (now - lastPlacedAt < 400) return;
    lastPlacedAt = now;

    const at = { lng: lngLat.lng, lat: lngLat.lat };
    if (placingCamera) {
      setPlacingCamera(false);
      openCameraEditor(at);
      return;
    }
    setPlacing(false);
    openEditor({ at });
  }

  map.on("click", (event) => placeAt(event.lngLat));
  map.on("touchend", (event) => {
    if (event.lngLat) placeAt(event.lngLat);
  });

  async function save() {
    errorEl.hidden = true;
    const label = labelInput.value.trim();
    const note = noteInput.value.trim();
    try {
      // "" clears an existing expiry; the server treats it as no expiry.
      const expires = el("mark-expires").value || "";
      if (editing) {
        await marks.update(editing.id, {
          label, note, color: chosenColor, expires_at: expires,
        });
        closeEditor();
      } else if (pending) {
        // Stay open after creating so files can be attached straight away.
        const created = await marks.create({
          ...pending, label, note, color: chosenColor, expires_at: expires,
        });
        editing = created;
        pending = null;
        el("mark-editor-title").textContent = "Edit mark";
        deleteButton.hidden = false;
        paintFiles(created);
      }
      onChange();
      tick();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  }

  el("mark-save").addEventListener("click", save);

  deleteButton.addEventListener("click", async () => {
    if (!editing) return;
    try {
      await marks.remove(editing.id);
      closeEditor();
      onChange();
      tick();
    } catch (error) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  });

  // Clicking an existing mark opens it — dispatched by the tooltip layer,
  // which is what actually owns picking.
  window.addEventListener("pulse:edit-mark", async (event) => {
    if (await ensureSignedIn()) openEditor({ mark: event.detail });
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      if (placing) setPlacing(false);
      if (placingCamera) setPlacingCamera(false);
      if (!editor.hidden) closeEditor();
      if (!camEditor.hidden) closeCameraEditor();
      if (!bEditor.hidden) closeBuildingEditor();
      return;
    }
    if (!isShortcut(event)) return;
    const key = event.key.toLowerCase();
    if (key === "m") {
      event.preventDefault();
      setPlacing(!placing);
    } else if (key === "c") {
      event.preventDefault();
      setPlacingCamera(!placingCamera);
    }
  });

  // ── Incident browser ────────────────────────────────────────────────────
  // A list is the only way to find the one incident you care about among
  // dozens of identical dots; clicking flies the camera to it.
  const browser = el("incident-browser");

  /**
   * Fly to an incident and look straight down at it.
   *
   * Pitch goes to zero deliberately. At the map's usual 55-58° lean, the point
   * you fly to sits behind whatever tower is between it and the camera, and
   * "take me there" lands you looking at a wall. Overhead, the location is
   * unambiguous — and bearing is zeroed too, so north is up and the street
   * grid reads the way every other map of Denver does.
   */
  function flyTo(row) {
    map.flyTo({
      center: [row.x, row.y],
      zoom: Math.max(map.getZoom(), 17.5),
      pitch: 0,
      bearing: 0,
      duration: 1400,
      essential: true,
    });
  }

  function paintBrowser() {
    const incidents = layers.find((l) => l.key === "incidents");
    if (!browser || !incidents || incidents.status !== "ready") return;

    // Newest first: on a live board the top of the list is the point.
    const recent = incidents.recent(getNow()).slice().reverse();
    el("browser-count").textContent = String(recent.length);
    browser.innerHTML = "";

    for (const row of recent.slice(0, 120)) {
      const item = document.createElement("div");
      item.className = "browser-row";
      item.setAttribute("role", "button");
      item.setAttribute("tabindex", "0");

      const when = document.createElement("span");
      when.className = "bt";
      // Denver time, like the range above it and the tooltip beside it. This
      // was UTC, which nobody noticed while every row was days old — a live
      // outage that started at 19:24 listing itself as 01:24, directly under a
      // header reading "→ 19:24", is the same six-hour lie made obvious.
      when.textContent = new Date(row.t).toLocaleTimeString("en-US", {
        timeZone: "America/Denver", hour: "2-digit", minute: "2-digit",
        hour12: false,
      });

      const label = document.createElement("span");
      label.className = "bl";
      label.textContent = String(row.k ?? row.c).replace(/[-_]+/g, " ");

      const category = document.createElement("span");
      category.className = "bc";
      category.textContent = row.c;

      item.append(when, label, category);
      item.title = `${label.textContent} — click to locate`;
      item.addEventListener("click", () => flyTo(row));
      item.addEventListener("keydown", (event) => {
        if (event.key === "Enter") flyTo(row);
      });
      browser.append(item);
    }
  }

  const breakdown = el("breakdown");

  function tick() {
    paintBrowser();

    // What the list actually covers, measured from the records on screen
    // rather than asserted by a heading.
    const incidentLayer = layers.find((l) => l.key === "incidents");
    const shown = incidentLayer?.status === "ready"
      ? incidentLayer.recent(getNow()) : [];
    const rangeEl = el("window-range");
    if (rangeEl) {
      rangeEl.textContent = formatWindow(shown);
      rangeEl.title = shown.length
        ? `${shown.length} incidents, oldest to newest. Each source covers the `
          + "24 hours ending at its own newest record, so a lagging feed and a "
          + "live one can both be current."
        : "No incidents in the feed's most recent 24-hour window.";
    }

    // Per-source freshness, spelled out. One blended number cannot describe a
    // stream where one half is days old by design and the other is minutes.
    if (lagEl) {
      const sources = incidentLayer?.sources;
      const records = sources?.find((s) => !s.live);
      // Also re-read on every tick: mounted once, this went stale the first
      // time a refresh brought in newer records.
      lagEl.textContent = formatLag(records ? records.latest : latest);
      lagEl.title = sources?.length
        ? sources.map((s) => {
          const age = s.latest ? formatLag(s.latest) || "CURRENT" : "no data";
          return `${s.label}: ${age.replace(/^LAG /, "")}`
            + `${s.stale ? " (upstream down, serving last good copy)" : ""}`;
        }).join("\n")
        : "Age of the newest official record in the feed.";
    }

    markToggle.setDetail(String(marks.count));

    for (const { members, control } of rows) {
      let count = 0;
      let anyReady = false;
      for (const layer of members) {
        if (layer.status === "ready") {
          anyReady = true;
          count += layer.visibleCount || layer.loadedCount;
        }
      }
      // "…" while a layer is still on its way, so a slow source reads as
      // loading rather than as empty.
      control.setDetail(anyReady ? String(count)
        : (members.every((l) => l.status === "unavailable") ? "OFF" : "…"));
    }

    const counts = {};
    const incidents = layers.find((l) => l.key === "incidents");
    if (incidents?.enabled && incidents.status === "ready") {
      // Counted before the filter, not after. Filtered counts would empty
      // every chip but the chosen one, and a chip showing zero is a chip you
      // cannot click your way back through.
      for (const row of incidents.inWindow(getNow())) {
        counts[row.c] = (counts[row.c] ?? 0) + 1;
      }
    }

    const available = Object.keys(counts);
    breakdown.innerHTML = "";
    for (const [key, value] of Object.entries(counts).sort((a, b) => b[1] - a[1])) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "chip-filter";
      chip.textContent = `${key} ${value}`;
      const on = incidents.categories?.has(key) ?? false;
      if (on) chip.classList.add("is-on");
      // Told, not just shown: the colour change alone does not reach a screen
      // reader, and does not say what clicking will do.
      chip.setAttribute("aria-pressed", String(on));
      chip.title = on
        ? `Showing ${key}. Click to stop filtering by it.`
        : `Show only ${key} incidents.`;
      chip.addEventListener("click", () => {
        incidents.toggleCategory(key, available);
        // Repaint the map, then the panel. The layer is the single source of
        // truth for what is shown, so both just re-read it.
        onChange?.();
        tick();
      });
      breakdown.append(chip);
    }

    const filterNote = el("breakdown-note");
    if (filterNote) {
      const chosen = incidents?.categories;
      filterNote.textContent = chosen
        ? `filtered to ${[...chosen].join(", ")} — click again to clear`
        : "";
    }
  }

  return { tick, paintLegend };
}
